import { Box, Button, Flex, Grid, Heading, Text, useDisclosure } from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";

import * as api from "@nens/threedi/openapi-client";

import { getV3Api } from "../../apiConfiguration";
import { OrganisationContext } from "../../providers/OrganisationProvider";
import { useOrganisationContract } from "../../queries/general";
import { useResultFiles } from "../../queries/simulations";

import SimulationPostProcessModal from "./SimulationPostProcessModal";

interface SimulationPostProcessDetailsProps {
  simulationPk: number;
  simulationName?: string;
}

const costTypeValues = { min: "Minimum", max: "Maximum", avg: "Average" };
const monthsValues = {
  jan: "January",
  feb: "February",
  mar: "March",
  apr: "April",
  may: "May",
  jun: "June",
  jul: "July",
  aug: "August",
  sep: "September",
  oct: "October",
  nov: "November",
  dec: "December",
};
const repairTimeValues: Record<number, string> = {
  1: "0 hour",
  6: "6 hours",
  24: "1 day",
  48: "2 days",
  120: "5 days",
  240: "10 days",
};

export default function SimulationPostProcessDetails({
  simulationPk,
  simulationName,
}: SimulationPostProcessDetailsProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { organisation } = useContext(OrganisationContext);
  const contract = useOrganisationContract(organisation.uniqueId);
  const includesLizardProcess = contract?.scope?.includes("lizardprocess");

  const filesResponse = useResultFiles(simulationPk);
  const results = filesResponse.data ? filesResponse.data.results : [];
  const result3diRemoved =
    results.find((result) => result.filename === "results_3di.nc")?.file?.state === "removed";

  const [postProcessingLizardResult, setPostProcessingLizardResult] =
    useState<api.BasicPostProcessing | null>();
  const [includesArrivalTime, setIncludesArrivalTime] = useState<"Yes" | "No" | "unknown">(
    "unknown"
  );
  const [postProcessingDamageResult, setPostProcessingDamageResult] =
    useState<api.DamagePostProcessing | null>();

  useEffect(() => {
    const v3Api = getV3Api();
    v3Api
      .simulationsResultsPostProcessingLizardBasicList({
        simulationPk: simulationPk,
      })
      .then((response) => setPostProcessingLizardResult(response))
      .catch(() => setPostProcessingLizardResult(null));

    v3Api
      .simulationsResultsPostProcessingLizardArrivalList({
        simulationPk: simulationPk,
      })
      .then(() => setIncludesArrivalTime("Yes"))
      .catch(() => setIncludesArrivalTime("No"));

    v3Api
      .simulationsResultsPostProcessingLizardDamageList({
        simulationPk: simulationPk,
      })
      .then((response) => setPostProcessingDamageResult(response))
      .catch(() => setPostProcessingDamageResult(null));
  }, [simulationPk]);

  return (
    <Box marginTop="18px">
      <Heading size="lg" marginBottom="8" marginTop="8">
        Postprocessing
      </Heading>
      <Grid gap="1rem" templateColumns="1fr 1fr">
        <Flex flexDirection="column">
          <Heading fontSize="md">Scenario in Lizard</Heading>
          {postProcessingLizardResult && (
            <>
              <p>Name: {postProcessingLizardResult.scenarioName || "unknown"}</p>
              <p>UUID: {postProcessingLizardResult.resultUuid}</p>
              <p>Includes arrival time: {includesArrivalTime}</p>
            </>
          )}
          {postProcessingLizardResult === null && (
            <>
              <Text m={0}>No scenario found. Create one here:</Text>
              <Button
                title={`${
                  result3diRemoved ? "Only simulations with raw results can be post-processed." : ""
                } ${
                  !includesLizardProcess
                    ? (result3diRemoved ? "Also this" : "This") +
                      " option is not included in your contract. Please contact the service desk if you want to add Rana live to your contract."
                    : ""
                }`}
                mt={1}
                maxWidth={250}
                onClick={onOpen}
                variant="secondary"
                isDisabled={!includesLizardProcess || result3diRemoved}
              >
                Post-process results
              </Button>
              <SimulationPostProcessModal
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={onClose}
                simulationName={simulationName}
                simulationPk={simulationPk}
              />
            </>
          )}
        </Flex>
        <Flex flexDirection="column">
          <Heading fontSize="md">Damage estimation</Heading>
          {postProcessingDamageResult && (
            <>
              <p>Cost type: {costTypeValues[postProcessingDamageResult.costType]}</p>
              <p>Flood month: {monthsValues[postProcessingDamageResult.floodMonth]}</p>
              <p>
                Inundation period:{" "}
                {repairTimeValues[Number(postProcessingDamageResult.inundationPeriod)]}
              </p>
              <p>
                Infrastructure repair time:{" "}
                {repairTimeValues[Number(postProcessingDamageResult.repairTimeInfrastructure)]}
              </p>
              <p>
                Buildings repair time:{" "}
                {repairTimeValues[Number(postProcessingDamageResult.repairTimeBuildings)]}
              </p>
            </>
          )}
          {postProcessingDamageResult === null && <p>No damage estimation found</p>}
        </Flex>
      </Grid>
    </Box>
  );
}
